import React, { useEffect, useState } from 'react';
import {
  CardBody,
  CardHeader,
  Container,
  Card,
  Row,
  Col,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Form,
  Label,
  FormFeedback,
  Table,
  Spinner,
} from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb'; // Adjust path as needed
import Select from 'react-select';
import api, { handleApiError } from '../../../helpers/api'; // Your error handler
import { isEmpty } from 'lodash';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CategoryManagement = () => {
  const { t } = useTranslation();
  document.title = t('category_management.title');

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [modal, setModal] = useState({ type: null, open: false, data: null });
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({}); // New state for tracking expanded categories
  const itemsPerPage = 10;

  // Toggle expand/collapse for a category
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Fetch all categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        const categoriesWithCounts = await Promise.all(
            response.data.data.map(async (cat) => {
              const productCount = await api.get(`/products?category=${cat._id}`).then((res) => res.data.data.length);
              return { ...cat, productCount };
            })
        );
        const categoryTree = buildCategoryTree(categoriesWithCounts);
        setCategories(categoryTree);
        setFilteredCategories(categoryTree);
      } catch (error) {
        handleApiError(error, null, (path) => (window.location.href = path));
        setError({ message: t('category_management.errors.fetch_categories'), code: 'ERR_FETCH_CATEGORIES' });
      }
    };
    fetchCategories();
  }, [t]);

  // Filter categories based on search query
  useEffect(() => {
    const filterCategories = (categories, query) => {
      if (!query) return categories;
      const lowerQuery = query.toLowerCase();
      const filterRecursive = (cats) =>
          cats
              .filter(
                  (cat) =>
                      cat.name.toLowerCase().includes(lowerQuery) ||
                      (cat.children && filterRecursive(cat.children).length > 0)
              )
              .map((cat) => ({
                ...cat,
                children: filterRecursive(cat.children || []),
              }));
      return filterRecursive(categories);
    };
    setFilteredCategories(filterCategories(categories, searchQuery));
    setCurrentPage(1); // Reset to first page on search
  }, [searchQuery, categories]);

  // Build tree structure for categories
  const buildCategoryTree = (categories) => {
    const map = {};
    const tree = [];

    categories.forEach((cat) => {
      map[cat._id] = { ...cat, children: [], productCount: cat.productCount || 0 };
    });

    categories.forEach((cat) => {
      if (cat.parent) {
        map[cat.parent._id]?.children.push(map[cat._id]);
        map[cat.parent._id].productCount += map[cat._id].productCount;
      } else {
        tree.push(map[cat._id]);
      }
    });

    return tree;
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Toggle modal
  const toggleModal = (type = null, data = null) => {
    setModal({ type, open: !!type, data });
    setError(null);
    if (type !== 'viewProducts') setProducts([]);
  };

  // Toggle dropdown
  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  // Handle Edit/Create Category
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { name, customizable, parent } = e.target.elements;
    const payload = {
      name: name.value,
      customizable: customizable.checked,
      parent: parent.value || null,
      slug: name.value.toLowerCase().replace(/\s+/g, '-'),
      possibleSpecs: [],
    };

    try {
      if (modal.type === 'editCategory') {
        await api.put(`/categories/${modal.data._id}`, payload);
      } else {
        await api.post('/categories', payload);
      }
      const response = await api.get('/categories');
      const categoriesWithCounts = await Promise.all(
          response.data.data.map(async (cat) => {
            const productCount = await api.get(`/products?category=${cat._id}`).then((res) => res.data.data.length);
            return { ...cat, productCount };
          })
      );
      setCategories(buildCategoryTree(categoriesWithCounts));
      toggleModal();
    } catch (error) {
      const errorMessage =
          error.response?.data?.code === 'ERR_DUPLICATE_NAME'
              ? t('category_management.errors.duplicate_name', { name: name.value })
              : error.response?.data?.code === 'ERR_VALIDATION'
                  ? t('category_management.errors.validation')
                  : t('category_management.errors.category_save', {
                    action:
                        modal.type === 'editCategory'
                            ? t('category_management.actions.update')
                            : t('category_management.actions.create'),
                  });
      setError({ message: errorMessage, code: error.response?.data?.code || 'ERR_CATEGORY_SAVE' });
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Category
  const handleDeleteCategory = async (categoryId, moveToCategory = null, deleteProducts = false) => {
    try {
      const category = await api.get(`/categories/${categoryId}`);
      const subcategories = await api
          .get('/categories')
          .then((res) => res.data.data.filter((cat) => cat.parent?._id === categoryId));
      if (subcategories.length > 0) {
        setError({ message: t('category_management.errors.subcategories_exist'), code: 'ERR_SUBCATEGORIES_EXIST' });
        return;
      }
      if (category.data.data.productCount > 0 && !moveToCategory && !deleteProducts) {
        setError({ message: t('category_management.errors.products_unhandled'), code: 'ERR_PRODUCTS_UNHANDLED' });
        return;
      }
      if (moveToCategory || deleteProducts) {
        await api.put(`/categories/${categoryId}/reassign`, { moveToCategory, deleteProducts });
      }
      await api.delete(`/categories/${categoryId}`);
      const response = await api.get('/categories');
      const categoriesWithCounts = await Promise.all(
          response.data.data.map(async (cat) => {
            const productCount = await api.get(`/products?category=${cat._id}`).then((res) => res.data.data.length);
            return { ...cat, productCount };
          })
      );
      setCategories(buildCategoryTree(categoriesWithCounts));
      toggleModal();
    } catch (error) {
      const errorMessage =
          error.response?.data?.code === 'ERR_NOT_FOUND'
              ? t('category_management.errors.category_not_found')
              : t('category_management.errors.category_delete');
      setError({ message: errorMessage, code: error.response?.data?.code || 'ERR_CATEGORY_DELETE' });
    }
  };

  // Handle Spec Definition Submit
  const handleSpecSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    const specs = modal.data.specs || [];
    const updatedSpecs = specs.map((spec, index) => ({
      ...spec,
      name: formData.get(`specName${index}`),
      multiple: formData.get(`multiple${index}`) === 'on',
      basePrice: formData.get(`basePrice${index}`) ? Number(formData.get(`basePrice${index}`)) : 0,
      options: spec.options.map((opt, optIndex) => ({
        name: formData.get(`optionName${index}-${optIndex}`),
        price: Number(formData.get(`optionPrice${index}-${optIndex}`)),
      })),
    }));

    try {
      await Promise.all(
          updatedSpecs.map((spec) =>
              api.patch(`/specDefinitions/${spec._id}`, {
                name: spec.name,
                multiple: spec.multiple,
                basePrice: spec.options.length > 0 ? null : spec.basePrice,
                options: spec.options,
                category: modal.data._id,
              })
          )
      );
      toggleModal();
    } catch (error) {
      const errorMessage =
          error.response?.data?.code === 'ERR_VALIDATION'
              ? t('category_management.errors.spec_validation')
              : t('category_management.errors.spec_save');
      setError({ message: errorMessage, code: error.response?.data?.code || 'ERR_SPEC_SAVE' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Spec Definitions
  const fetchSpecs = async (categoryId) => {
    try {
      const response = await api.get(`/specDefinitions?category=${categoryId}`);
      return response.data.data;
    } catch (error) {
      setError({ message: t('category_management.errors.spec_fetch'), code: 'ERR_SPEC_FETCH' });
      return [];
    }
  };

  // Fetch Products for View Products Modal
  const fetchProducts = async (categoryId) => {
    try {
      const response = await api.get(`/products?category=${categoryId}`);
      setProducts(response.data.data);
    } catch (error) {
      setError({ message: t('category_management.errors.product_fetch'), code: 'ERR_PRODUCT_FETCH' });
    }
  };

  // Render category in list view
  const renderCategoryList = (category, level = 0) => (
      <React.Fragment key={category._id}>
        <tr style={{ backgroundColor: level > 0 ? '#f8f9fa' : 'white', transition: 'background-color 0.2s' }}>
          <td style={{ padding: '12px 15px', paddingLeft: `${level * 20 + 15}px`, fontWeight: level === 0 ? '600' : 'normal', borderBottom: '1px solid #e9ecef' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {category.children.length > 0 && (
                  <i
                      className={`ri-${expandedCategories[category._id] ? 'subtract' : 'add'}-line me-2`}
                      style={{ cursor: 'pointer', color: '#6c757d' }}
                      onClick={() => toggleCategory(category._id)}
                  ></i>
              )}
              <i className={`ri-folder-line me-2 ${level > 0 ? 'text-muted' : 'text-primary'}`}></i>
              {category.name} <span className="text-muted">({category.productCount})</span>
            </div>
          </td>
          <td style={{ padding: '12px 15px', borderBottom: '1px solid #e9ecef' }}>
            <Button
                color="primary"
                size="sm"
                onClick={() => {
                  toggleModal('viewProducts', category);
                  fetchProducts(category._id);
                }}
                style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '4px' }}
            >
              {t('category_management.actions.view_products')}
            </Button>
          </td>
          <td style={{ padding: '12px 15px', borderBottom: '1px solid #e9ecef', textAlign: 'right' }}>
            <Dropdown isOpen={dropdownOpen === category._id} toggle={() => toggleDropdown(category._id)}>
              <DropdownToggle tag="button" className="btn btn-light btn-sm" style={{ padding: '6px', borderRadius: '4px' }}>
                <i className="ri-more-2-fill"></i>
              </DropdownToggle>
              <DropdownMenu end style={{ minWidth: '150px' }}>
                <DropdownItem onClick={() => toggleModal('editCategory', category)}>
                  {t('category_management.actions.edit_category')}
                </DropdownItem>
                <DropdownItem
                    onClick={async () => toggleModal('editSpecs', { ...category, specs: await fetchSpecs(category._id) })}
                    disabled={!category.customizable}
                    style={{ opacity: category.customizable ? 1 : 0.5, cursor: category.customizable ? 'pointer' : 'not-allowed' }}
                >
                  {t('category_management.actions.edit_specs')}
                </DropdownItem>
                <DropdownItem onClick={() => toggleModal('deleteCategory', category)}>
                  {t('category_management.actions.delete_category')}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </td>
        </tr>
        {expandedCategories[category._id] &&
            category.children.map((child) => renderCategoryList(child, level + 1))}
      </React.Fragment>
  );

  // Render category in grid view
  const renderCategoryGrid = (category, level = 0) => (
      <React.Fragment key={category._id}>
        <Col xs={12} sm={6} md={4} lg={3} className="mb-3">
          <Card style={{ marginLeft: `${level * 20}px`, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px', overflow: 'hidden' }}>
            <CardBody style={{ padding: '15px' }}>
              <h6 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                {category.children.length > 0 && (
                    <i
                        className={`ri-${expandedCategories[category._id] ? 'subtract' : 'add'}-line me-2`}
                        style={{ cursor: 'pointer', color: '#6c757d' }}
                        onClick={() => toggleCategory(category._id)}
                    ></i>
                )}
                <i className={`ri-folder-line me-2 ${level > 0 ? 'text-muted' : 'text-primary'}`}></i>
                {category.name}
              </h6>
              <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
                {t('category_management.products')}: {category.productCount}
              </p>
              <div className="d-flex gap-2">
                <Button
                    color="primary"
                    size="sm"
                    onClick={() => {
                      toggleModal('viewProducts', category);
                      fetchProducts(category._id);
                    }}
                    style={{ fontSize: '12px', padding: '5px 10px', borderRadius: '4px' }}
                >
                  {t('category_management.actions.view_products')}
                </Button>
                <Dropdown isOpen={dropdownOpen === category._id} toggle={() => toggleDropdown(category._id)}>
                  <DropdownToggle tag="button" className="btn btn-light btn-sm" style={{ padding: '5px', borderRadius: '4px' }}>
                    <i className="ri-more-2-fill"></i>
                  </DropdownToggle>
                  <DropdownMenu end style={{ minWidth: '150px' }}>
                    <DropdownItem onClick={() => toggleModal('editCategory', category)}>
                      {t('category_management.actions.edit_category')}
                    </DropdownItem>
                    <DropdownItem
                        onClick={async () => toggleModal('editSpecs', { ...category, specs: await fetchSpecs(category._id) })}
                        disabled={!category.customizable}
                        style={{ opacity: category.customizable ? 1 : 0.5, cursor: category.customizable ? 'pointer' : 'not-allowed' }}
                    >
                      {t('category_management.actions.edit_specs')}
                    </DropdownItem>
                    <DropdownItem onClick={() => toggleModal('deleteCategory', category)}>
                      {t('category_management.actions.delete_category')}
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        </Col>
        {expandedCategories[category._id] &&
            category.children.map((child) => renderCategoryGrid(child, level + 1))}
      </React.Fragment>
  );

  return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('category_management.title')} pageTitle={t('category_management.page_title')} />
          <Card style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: '8px' }}>
            <CardHeader style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef', padding: '15px' }}>
              <Row className="g-2 align-items-center">
                <Col xl={3}>
                  <div style={{ position: 'relative' }}>
                    <Input
                        type="text"
                        className="form-control search"
                        placeholder={t('category_management.search_placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ paddingRight: '30px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '14px' }}
                    />
                    <i
                        className="ri-search-line"
                        style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', color: '#6c757d' }}
                    ></i>
                  </div>
                </Col>
                <Col xxl={3} className="ms-auto d-flex gap-2">
                  <Button
                      color={viewMode === 'list' ? 'primary' : 'light'}
                      onClick={() => setViewMode('list')}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px' }}
                  >
                    <i className="ri-list-check me-1"></i> {t('category_management.actions.list_view')}
                  </Button>
                  <Button
                      color={viewMode === 'grid' ? 'primary' : 'light'}
                      onClick={() => setViewMode('grid')}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px' }}
                  >
                    <i className="ri-grid-fill me-1"></i> {t('category_management.actions.grid_view')}
                  </Button>
                  <Button
                      color="success"
                      onClick={() => toggleModal('createCategory')}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px' }}
                  >
                    <i className="ri-add-fill me-1 align-bottom"></i> {t('category_management.actions.add_category')}
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody style={{ padding: '20px' }}>
              {viewMode === 'list' ? (
                  <Table hover style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                    <tbody>{currentCategories.map((category) => renderCategoryList(category))}</tbody>
                  </Table>
              ) : (
                  <Row>{currentCategories.map((category) => renderCategoryGrid(category))}</Row>
              )}
              <Row className="mt-3">
                <Col sm={6}>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCategories.length)} of{' '}
                    {filteredCategories.length} categories
                  </p>
                </Col>
                <Col sm={6} className="text-sm-end">
                  <ul style={{ listStyle: 'none', display: 'flex', justifyContent: 'flex-end', gap: '5px', margin: 0 }}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <li key={page}>
                          <Button
                              color={currentPage === page ? 'primary' : 'light'}
                              onClick={() => paginate(page)}
                              style={{
                                padding: '5px 10px',
                                margin: '0 2px',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                backgroundColor: currentPage === page ? '#405189' : '#fff',
                                color: currentPage === page ? '#fff' : '#000',
                                fontSize: '13px',
                              }}
                          >
                            {page}
                          </Button>
                        </li>
                    ))}
                  </ul>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Edit/Create Category Modal */}
          <Modal isOpen={modal.type === 'editCategory' || modal.type === 'createCategory'} toggle={() => toggleModal()}>
            <ModalHeader toggle={() => toggleModal()} style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
              {modal.type === 'editCategory' ? t('category_management.modal.edit_category') : t('category_management.modal.create_category')}
            </ModalHeader>
            <ModalBody style={{ padding: '20px' }}>
              <Form onSubmit={handleCategorySubmit}>
                <div className="mb-3">
                  <Label htmlFor="name" className="form-label" style={{ fontSize: '14px', fontWeight: '500' }}>
                    {t('category_management.form.category_name')}
                  </Label>
                  <Input
                      type="text"
                      name="name"
                      className="form-control"
                      defaultValue={modal.data?.name || ''}
                      placeholder={t('category_management.form.category_name_placeholder')}
                      required
                      style={{ border: '1px solid #ced4da', borderRadius: '4px', padding: '8px' }}
                  />
                  {error && (
                      <FormFeedback style={{ display: 'block', fontSize: '12px', color: '#dc3545' }}>
                        {error.message} {error.code && `(${error.code})`}
                      </FormFeedback>
                  )}
                </div>
                <div className="mb-3">
                  <Label className="form-label" style={{ fontSize: '14px', fontWeight: '500' }}>
                    {t('category_management.form.customizable')}
                  </Label>
                  <Input
                      type="checkbox"
                      name="customizable"
                      defaultChecked={modal.data?.customizable || false}
                      style={{ marginLeft: '10px' }}
                  />
                </div>
                <div className="mb-3">
                  <Label htmlFor="parent" className="form-label" style={{ fontSize: '14px', fontWeight: '500' }}>
                    {t('category_management.form.parent_category')}
                  </Label>
                  <Select
                      name="parent"
                      options={[{ label: t('category_management.form.none'), value: '' }, ...categories.map((cat) => ({ label: cat.name, value: cat._id }))]}
                      defaultValue={
                        modal.data?.parent
                            ? { label: modal.data.parent.name, value: modal.data.parent._id }
                            : { label: t('category_management.form.none'), value: '' }
                      }
                      placeholder={t('category_management.form.parent_category_placeholder')}
                      styles={{
                        control: (base) => ({ ...base, borderRadius: '4px', border: '1px solid #ced4da', padding: '2px' }),
                      }}
                  />
                </div>
                <div className="hstack gap-2 justify-content-end">
                  <Button
                      color="light"
                      onClick={() => toggleModal()}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ced4da' }}
                  >
                    {t('category_management.actions.cancel')}
                  </Button>
                  <Button
                      type="submit"
                      color="primary"
                      disabled={loading}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', backgroundColor: '#405189', border: 'none' }}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    {t('category_management.actions.save')}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

          {/* Edit Spec Definitions Modal */}
          <Modal isOpen={modal.type === 'editSpecs'} toggle={() => toggleModal()} size="lg">
            <ModalHeader toggle={() => toggleModal()} style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
              {t('category_management.modal.edit_specs')}
            </ModalHeader>
            <ModalBody style={{ padding: '20px' }}>
              <Form onSubmit={handleSpecSubmit}>
                {modal.data?.specs?.map((spec, index) => (
                    <div key={spec._id} style={{ borderBottom: '1px solid #e9ecef', paddingBottom: '15px', marginBottom: '15px' }}>
                      <Row>
                        <Col md={4}>
                          <Label htmlFor={`specName${index}`} style={{ fontSize: '14px', fontWeight: '500' }}>
                            {t('category_management.form.spec_name')}
                          </Label>
                          <Input
                              type="text"
                              name={`specName${index}`}
                              defaultValue={spec.name}
                              className="form-control"
                              required
                              style={{ border: '1px solid #ced4da', borderRadius: '4px', padding: '8px' }}
                          />
                        </Col>
                        <Col md={3}>
                          <Label style={{ fontSize: '14px', fontWeight: '500' }}>
                            {t('category_management.form.multiple')}
                          </Label>
                          <Input
                              type="checkbox"
                              name={`multiple${index}`}
                              defaultChecked={spec.multiple}
                              style={{ marginLeft: '10px' }}
                              onChange={(e) => {
                                const basePriceInput = document.getElementById(`basePrice${index}`);
                                if (basePriceInput) basePriceInput.disabled = e.target.checked || spec.options.length > 0;
                              }}
                          />
                        </Col>
                        <Col md={3}>
                          <Label htmlFor={`basePrice${index}`} style={{ fontSize: '14px', fontWeight: '500' }}>
                            {t('category_management.form.base_price')}
                          </Label>
                          <Input
                              type="number"
                              name={`basePrice${index}`}
                              id={`basePrice${index}`}
                              defaultValue={spec.basePrice || ''}
                              className="form-control"
                              disabled={spec.multiple || spec.options.length > 0}
                              min="0"
                              placeholder={t('category_management.form.base_price_placeholder')}
                              style={{ border: '1px solid #ced4da', borderRadius: '4px', padding: '8px' }}
                              onKeyDown={(e) => {
                                if (e.key === '-' || e.key === 'e') e.preventDefault();
                              }}
                          />
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                          <Button
                              color="danger"
                              size="sm"
                              onClick={async () => {
                                await api.delete(`/specDefinitions/${spec._id}`);
                                toggleModal('editSpecs', { ...modal.data, specs: await fetchSpecs(modal.data._id) });
                              }}
                              style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px' }}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </Col>
                      </Row>
                      {spec.options?.length > 0 && (
                          <div style={{ marginTop: '15px' }}>
                            <h6 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>{t('category_management.form.options')}</h6>
                            {spec.options.map((option, optIndex) => (
                                <Row key={optIndex} className="mb-2">
                                  <Col md={5}>
                                    <Input
                                        type="text"
                                        name={`optionName${index}-${optIndex}`}
                                        defaultValue={option.name}
                                        className="form-control"
                                        placeholder={t('category_management.form.option_name_placeholder')}
                                        required
                                        style={{ border: '1px solid #ced4da', borderRadius: '4px', padding: '8px' }}
                                    />
                                  </Col>
                                  <Col md={5}>
                                    <Input
                                        type="number"
                                        name={`optionPrice${index}-${optIndex}`}
                                        defaultValue={option.price}
                                        className="form-control"
                                        placeholder={t('category_management.form.option_price_placeholder')}
                                        required
                                        min="0"
                                        style={{ border: '1px solid #ced4da', borderRadius: '4px', padding: '8px' }}
                                        onKeyDown={(e) => {
                                          if (e.key === '-' || e.key === 'e') e.preventDefault();
                                        }}
                                    />
                                  </Col>
                                  <Col md={2}>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => {
                                          const updatedSpecs = [...modal.data.specs];
                                          updatedSpecs[index].options.splice(optIndex, 1);
                                          toggleModal('editSpecs', { ...modal.data, specs: updatedSpecs });
                                          const basePriceInput = document.getElementById(`basePrice${index}`);
                                          if (basePriceInput) basePriceInput.disabled = updatedSpecs[index].multiple;
                                        }}
                                        style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px' }}
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </Button>
                                  </Col>
                                </Row>
                            ))}
                            <Button
                                color="success"
                                size="sm"
                                onClick={() => {
                                  const updatedSpecs = [...modal.data.specs];
                                  updatedSpecs[index].options.push({ name: '', price: 0 });
                                  toggleModal('editSpecs', { ...modal.data, specs: updatedSpecs });
                                  const basePriceInput = document.getElementById(`basePrice${index}`);
                                  if (basePriceInput) basePriceInput.disabled = true;
                                }}
                                style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px', marginTop: '10px' }}
                            >
                              {t('category_management.actions.add_option')}
                            </Button>
                          </div>
                      )}
                      {spec.rules?.length > 0 && (
                          <div style={{ marginTop: '15px' }}>
                            <h6 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>{t('category_management.form.rules')}</h6>
                            {spec.rules.map((rule, ruleIndex) => (
                                <div key={ruleIndex} style={{ marginBottom: '10px', fontSize: '14px' }}>
                          <span>
                            {rule.constraint.rule}: {rule.constraint.value || 'N/A'} ({rule.errorMessage})
                          </span>
                                </div>
                            ))}
                          </div>
                      )}
                    </div>
                ))}
                <Button
                    color="success"
                    size="sm"
                    onClick={async () => {
                      const newSpec = await api.post('/specDefinitions', {
                        name: t('category_management.form.new_spec'),
                        category: modal.data._id,
                        multiple: false,
                        options: [],
                      });
                      toggleModal('editSpecs', { ...modal.data, specs: [...modal.data.specs, newSpec.data.data] });
                    }}
                    style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px', marginBottom: '15px' }}
                >
                  {t('category_management.actions.add_spec')}
                </Button>
                <div className="hstack gap-2 justify-content-end">
                  <Button
                      color="light"
                      onClick={() => toggleModal()}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ced4da' }}
                  >
                    {t('category_management.actions.cancel')}
                  </Button>
                  <Button
                      type="submit"
                      color="primary"
                      disabled={loading}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', backgroundColor: '#405189', border: 'none' }}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    {t('category_management.actions.save')}
                  </Button>
                </div>
                {error && (
                    <FormFeedback style={{ display: 'block', fontSize: '12px', color: '#dc3545' }}>
                      {error.message} {error.code && `(${error.code})`}
                    </FormFeedback>
                )}
              </Form>
            </ModalBody>
          </Modal>

          {/* Delete Category Modal */}
          <Modal isOpen={modal.type === 'deleteCategory'} toggle={() => toggleModal()}>
            <ModalHeader toggle={() => toggleModal()} style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
              {t('category_management.modal.delete_category')}
            </ModalHeader>
            <ModalBody style={{ padding: '20px' }}>
              <p style={{ fontSize: '14px', marginBottom: '15px' }}>
                {t('category_management.modal.delete_category_message')}
              </p>
              {modal.data?.productCount > 0 && (
                  <p style={{ fontSize: '14px', marginBottom: '15px' }}>
                    {t('category_management.modal.choose_products_action')}
                  </p>
              )}
              <Form>
                {modal.data?.productCount > 0 && (
                    <>
                      <div className="mb-3">
                        <Label htmlFor="moveToCategory" className="form-label" style={{ fontSize: '14px', fontWeight: '500' }}>
                          {t('category_management.form.move_to_category')}
                        </Label>
                        <Select
                            name="moveToCategory"
                            options={categories
                                .filter((cat) => cat._id !== modal.data?._id)
                                .map((cat) => ({ label: cat.name, value: cat._id }))}
                            placeholder={t('category_management.form.move_to_category_placeholder')}
                            styles={{
                              control: (base) => ({ ...base, borderRadius: '4px', border: '1px solid #ced4da', padding: '2px' }),
                            }}
                        />
                      </div>
                      <div className="mb-3">
                        <Label style={{ fontSize: '14px', fontWeight: '500' }}>
                          <Input
                              type="checkbox"
                              name="deleteProducts"
                              onChange={(e) =>
                                  toggleModal('deleteCategory', {
                                    ...modal.data,
                                    deleteProducts: e.target.checked,
                                  })
                              }
                              style={{ marginRight: '10px' }}
                          />
                          {t('category_management.form.delete_products')}
                        </Label>
                      </div>
                    </>
                )}
                {error && (
                    <FormFeedback style={{ display: 'block', fontSize: '12px', color: '#dc3545', marginBottom: '15px' }}>
                      {error.message} {error.code && `(${error.code})`}
                    </FormFeedback>
                )}
                <div className="hstack gap-2 justify-content-end">
                  <Button
                      color="light"
                      onClick={() => toggleModal()}
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ced4da' }}
                  >
                    {t('category_management.actions.cancel')}
                  </Button>
                  <Button
                      color="danger"
                      onClick={() =>
                          handleDeleteCategory(
                              modal.data._id,
                              modal.data?.productCount > 0 ? document.querySelector('[name="moveToCategory"]')?.value : null,
                              modal.data?.deleteProducts
                          )
                      }
                      style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px' }}
                  >
                    {t('category_management.actions.delete')}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

          {/* View Products Modal */}
          <Modal isOpen={modal.type === 'viewProducts'} toggle={() => toggleModal()} size="lg">
            <ModalHeader toggle={() => toggleModal()} style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
              {t('category_management.modal.view_products', { categoryName: modal.data?.name })}
            </ModalHeader>
            <ModalBody style={{ padding: '20px' }}>
              {products.length === 0 ? (
                  <p style={{ fontSize: '14px' }}>{t('category_management.modal.no_products')}</p>
              ) : (
                  <Table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                    <thead>
                    <tr>
                      <th style={{ padding: '12px 15px', borderBottom: '1px solid #e9ecef', fontSize: '14px', fontWeight: '500' }}>
                        {t('category_management.table.name')}
                      </th>
                      <th style={{ padding: '12px 15px', borderBottom: '1px solid #e9ecef', fontSize: '14px', fontWeight: '500' }}>
                        {t('category_management.table.stock')}
                      </th>
                      <th style={{ padding: '12px 15px', borderBottom: '1px solid #e9ecef', fontSize: '14px', fontWeight: '500' }}>
                        {t('category_management.table.price')}
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    {products.map((product) => (
                        <tr key={product._id} style={{ backgroundColor: 'white', transition: 'background-color 0.2s' }}>
                          <td style={{ padding: '12px 15px', fontSize: '14px' }}>
                            <Link to={`/apps-ecommerce-product-details/${product._id}`} style={{ color: '#405189', textDecoration: 'none' }}>
                              {product.name}
                            </Link>
                          </td>
                          <td style={{ padding: '12px 15px', fontSize: '14px' }}>{product.stock}</td>
                          <td style={{ padding: '12px 15px', fontSize: '14px' }}>{product.price}</td>
                        </tr>
                    ))}
                    </tbody>
                  </Table>
              )}
              {error && (
                  <FormFeedback style={{ display: 'block', fontSize: '12px', color: '#dc3545', marginTop: '15px' }}>
                    {error.message} {error.code && `(${error.code})`}
                  </FormFeedback>
              )}
              <div className="hstack gap-2 justify-content-end mt-3">
                <Button
                    color="light"
                    onClick={() => toggleModal()}
                    style={{ padding: '8px 15px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ced4da' }}
                >
                  {t('category_management.actions.close')}
                </Button>
              </div>
            </ModalBody>
          </Modal>
        </Container>
      </div>
  );
};

export default CategoryManagement;